import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { captureFeatureError } from '@/lib/sentry-features'
import type { Notification, NotificationRecipientRole } from '@/types'

export function useNotifications(recipientRole: NotificationRecipientRole, recipientId?: string) {
  const qc = useQueryClient()
  const QK = ['notifications', { recipientRole, recipientId: recipientId ?? null }] as const

  const { data: notifications = [], isPending, isError, refetch } = useQuery({
    queryKey: QK,
    refetchInterval: 30000, // Auto-refresh every 30 seconds (era setInterval)
    queryFn: async () => {
      // Inbox rules:
      //  • Role-wide blasts for this role (e.g. all coordinators)
      //  • `all` notifications broadcast to every role
      //  • Direct-to-user notifications (e.g. @mentions) targeted by recipient_id
      const filters = [
        `recipient_role.eq.${recipientRole}`,
        `recipient_role.eq.all`,
      ]
      if (recipientId) filters.push(`recipient_id.eq.${recipientId}`)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .or(filters.join(','))
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data || []) as Notification[]
    },
  })

  const unreadCount = notifications.filter(n => !n.is_read).length

  const markAsReadM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }) },
  })

  const markAllAsReadM = useMutation({
    mutationFn: async () => {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id)
      if (unreadIds.length === 0) return
      const { error } = await supabase.from('notifications').update({ is_read: true }).in('id', unreadIds)
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }) },
  })

  const markAsRead = async (id: string) => { try { await markAsReadM.mutateAsync(id) } catch { /* ignore */ } }
  const markAllAsRead = async () => { try { await markAllAsReadM.mutateAsync() } catch { /* ignore */ } }

  return { notifications, loading: isPending, isError, refetch, markAsRead, markAllAsRead, unreadCount }
}

export async function createNotification(notification: {
  recipient_role: NotificationRecipientRole
  recipient_id?: string
  type: string
  title: string
  message?: string
  delivery_id?: string
  campaign_id?: string
  metadata?: Record<string, unknown>
}) {
  // Non-blocking: notificação é side-effect. Mas o erro precisa ser visível —
  // antes o `{ error }` era descartado e a notificação sumia em silêncio.
  const { error } = await supabase.from('notifications').insert(notification)
  if (error) {
    captureFeatureError(error, 'delivery', { stage: 'create_notification', type: notification.type })
  }
}
